/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { CollapsibleContent, CollapsibleTrigger, Root } from '@radix-ui/react-collapsible';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CollapsibleItem = {
    id: string;
    label: string;
};

export interface CollapsibleProps extends ComponentPropsWithoutRef<typeof Root> {
    title?: string;
    items?: CollapsibleItem[];
    triggerIcon?: ReactNode;
    showTriggerIcon?: boolean;
    className?: string;
    triggerClassName?: string;
    contentClassName?: string;
    itemClassName?: string;
}

const CollapsibleRoot = Root;

export default function Collapsible({
    title = 'Title',
    items = [{ id: 'item-1', label: 'Label' }],
    triggerIcon,
    showTriggerIcon = true,
    className,
    triggerClassName,
    contentClassName,
    itemClassName,
    ...props
}: CollapsibleProps) {
    return (
        <CollapsibleRoot className={cn('flex w-[350px] flex-col items-center gap-2', className)} {...props}>
            <CollapsibleTrigger
                className={cn(
                    'flex w-full items-center gap-4 px-4 py-0.5 text-left text-sm leading-5 font-semibold text-foreground',
                    triggerClassName
                )}>
                <span className="min-w-0 flex-1">{title}</span>
                {showTriggerIcon
                    ? (triggerIcon ?? <ChevronsUpDown className="size-4 text-foreground" aria-hidden="true" />)
                    : null}
            </CollapsibleTrigger>

            <CollapsibleContent className={cn('w-full space-y-2', contentClassName)}>
                {items.map((item) => (
                    <div
                        key={item.id}
                        className={cn(
                            'w-full rounded-xs border border-input px-4 py-3 text-sm leading-5 font-normal text-foreground',
                            itemClassName
                        )}>
                        {item.label}
                    </div>
                ))}
            </CollapsibleContent>
        </CollapsibleRoot>
    );
}

export { CollapsibleRoot, CollapsibleTrigger, CollapsibleContent };
